import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Share2, Target, Waypoints, Server } from 'lucide-react';
import {
  Badge,
  EmptyState,
  PageHeader,
  Select,
  Spinner,
  type SelectOption,
} from '../../ui';
import {
  useGraph,
  GRAPH_MAX_DEPTH,
  GRAPH_MIN_DEPTH,
} from '../../features/graph/useGraph';
import { nodeKindLabel, type GraphNode } from '../../features/graph/graphModel';
import { GraphView } from './GraphView';

/** Root-kind icon for the selector option prefix. */
const ROOT_KIND_LABEL: Record<string, string> = {
  goal: 'Goal',
  workstream: 'Workstream',
  service: 'Service',
};

/**
 * Graph — the Entity Graph explorer (section 23). Pick a goal, workstream or
 * service and see its connected slice of the Goal → Knowledge → Execution graph
 * (plus the people connected to that work), assembled CLIENT-SIDE from the
 * cross-reference ids stored on each entity. Click any node to re-centre the
 * view; open the ↗ affordance to jump to the entity. Awareness + discovery
 * only — it maps connections, never scores people.
 */
export function GraphPage() {
  const navigate = useNavigate();
  const {
    graph,
    roots,
    rootKey,
    setRootKey,
    depth,
    setDepth,
    subgraph,
    loading,
    error,
  } = useGraph();

  const rootOptions = useMemo<SelectOption[]>(
    () =>
      roots.map((r) => ({
        value: r.key,
        label: `${ROOT_KIND_LABEL[r.kind] ?? nodeKindLabel(r.kind)} · ${r.title}`,
      })),
    [roots],
  );

  const depthOptions = useMemo<SelectOption[]>(() => {
    const opts: SelectOption[] = [];
    for (let d = GRAPH_MIN_DEPTH; d <= GRAPH_MAX_DEPTH; d += 1) {
      opts.push({ value: String(d), label: `${d} hop${d === 1 ? '' : 's'}` });
    }
    return opts;
  }, []);

  const openNode = (node: GraphNode) => {
    if (node.route) navigate(node.route);
  };

  const totalNodes = graph.nodes.size;
  const totalEdges = graph.edges.length;

  return (
    <div>
      <PageHeader
        icon={<Share2 className="h-5 w-5" />}
        title="Entity Graph"
        description="Explore how goals, work, knowledge and people connect — assembled from the links recorded across the team's entities."
        actions={
          totalNodes > 0 ? (
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Badge tone="neutral">{totalNodes} nodes</Badge>
              <Badge tone="neutral">{totalEdges} relationships</Badge>
            </div>
          ) : undefined
        }
      />

      {/* Controls */}
      {!loading && !error && roots.length > 0 && (
        <div className="mb-5 flex flex-wrap items-end gap-3">
          <div className="w-96 max-w-full">
            <Select
              label="Start from"
              value={rootKey}
              options={rootOptions}
              onChange={(e) => setRootKey(e.target.value)}
            />
          </div>
          <div className="w-32">
            <Select
              label="Depth"
              value={String(depth)}
              options={depthOptions}
              onChange={(e) => setDepth(Number(e.target.value))}
            />
          </div>
          {/* Legend */}
          <div className="ml-auto flex items-center gap-3 self-center text-[11px] text-zinc-500">
            <span className="inline-flex items-center gap-1.5">
              <Target className="h-3.5 w-3.5 text-accent-fg" aria-hidden="true" /> Goals
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Waypoints className="h-3.5 w-3.5 text-sky-300" aria-hidden="true" /> Work
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Server className="h-3.5 w-3.5 text-emerald-300" aria-hidden="true" /> People/Services
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Assembling the entity graph" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load the graph"
          description={error}
        />
      ) : roots.length === 0 ? (
        <EmptyState
          icon={<Share2 className="h-6 w-6" />}
          title="Nothing to graph yet"
          description="Create goals, workstreams or services and link them to work, documents and decisions — the graph builds itself from those connections."
        />
      ) : subgraph ? (
        <GraphView
          graph={graph}
          subgraph={subgraph}
          onSelectNode={setRootKey}
          onOpenNode={openNode}
        />
      ) : (
        <EmptyState
          icon={<Share2 className="h-6 w-6" />}
          title="Select a starting point"
          description="Choose a goal, workstream or service to explore its connected graph."
        />
      )}
    </div>
  );
}

export default GraphPage;
